import { inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom, mapResponse } from '@ngrx/operators';
import { switchMap, delay, map, filter, tap, exhaustMap, of } from 'rxjs';
import { FileManagerEntitaService, FileManagerService, FileManagerTipologieService } from '../../../api/services';
import { fileManagerAction } from './file-manager.actions';
import { LoadingState } from '../models/loader.model';
import { downloadBlob } from '../shared-utils';
import { Store } from '@ngrx/store';
import { fileManagerSelectors } from './file-manager.selectors';
import { getErrorMessage, ZetraError } from '../../shared/errors/error-messages.helper';
import { TimelineStyle } from 'primeng/timeline';

export const fileManagerStatsLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.statsLoad),
      switchMap((props) =>
        fmService.mediaControllerStatsFilesAndFolders(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => fileManagerAction.statsDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              fileManagerAction.statsDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerRecentLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.recentsLoad),
      switchMap((props) =>
        fmService.mediaControllerRecentFilesAndFolders(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => fileManagerAction.recentsDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              fileManagerAction.recentsDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerFilesLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.folderLoad),
      switchMap((props) =>
        fmService.mediaControllerListFilesAndFolders(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => fileManagerAction.folderDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              fileManagerAction.folderDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerDownloadLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.downloadOne),
      switchMap((props) =>
        fmService.mediaControllerDownload(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => {
              downloadBlob(r, props.name!);
              return fileManagerAction.downloadOneDone({
                data: { fileData: r },
                state: { value: LoadingState.SUCCESS },
              });
            },
            error: (error: ZetraError) =>
              fileManagerAction.downloadOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerMkFolderLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.mkFolder),
      switchMap((props) =>
        fmService.mediaControllerMakeDir(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              fileManagerAction.mkFolderDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
                prefix: props.body.prefix!,
              }),
            error: (error: ZetraError) =>
              fileManagerAction.mkFolderDone({
                prefix: props.body.prefix!,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerDeleteOneLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.deleteOne),
      switchMap((props) =>
        fmService.mediaControllerDeleteFile(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => {
              return fileManagerAction.deleteOneDone({
                prefix: props.prefix,
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              });
            },

            error: (error: ZetraError) =>
              fileManagerAction.deleteOneDone({
                prefix: props.prefix,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const fileManagerUploadOneLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.uploadOne),
      switchMap((props) => {
        return fmService.mediaControllerUploadFile(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              fileManagerAction.uploadOneDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              fileManagerAction.uploadOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const fileManagerUploadEntityOneLoadEffect = createEffect(
  (actions$ = inject(Actions), fmService = inject(FileManagerEntitaService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.uploadEntityOne),
      switchMap((props) => {
        return fmService.entityMediaControllerUploadAndAttach(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              fileManagerAction.uploadEntityOneDone({
                entity: props.entity,
                entityId: props.entityId,
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              fileManagerAction.uploadEntityOneDone({
                entity: props.entity,
                entityId: props.entityId,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
                data: undefined,
              }),
          }),
        );
      }),
    );
  },
  { functional: true },
);

// export const uploadEntityOneDoneEffect = createEffect(
//   (actions$ = inject(Actions)) => {
//     return actions$.pipe(
//       ofType(fileManagerAction.uploadEntityOneDone),
//       map((props) =>
//         fileManagerAction.loadOneMedia({ entityId: props.entityId, entity: props.entity, skip: 0, limit: 10 }),
//       ),
//     );
//   },
//   { functional: true },
// );

export const fileManagerMkFolderDoneEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.mkFolderDone),
      filter((props) => props.state.value === LoadingState.SUCCESS),
      tap(({ prefix }) =>
        store.dispatch(
          fileManagerAction.folderLoad({
            prefix,
            query: '',
            recursive: 'false',
          }),
        ),
      ),
    );
  },
  { functional: true, dispatch: false },
);
export const fileManagerDeleteOneDoneEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.deleteOneDone),
      filter((props) => props.state.value === LoadingState.SUCCESS),
      tap(({ prefix }) =>
        store.dispatch(
          fileManagerAction.folderLoad({
            prefix,
            query: '',
            recursive: 'false',
          }),
        ),
      ),
    );
  },
  { functional: true, dispatch: false },
);

export const mediaTypesLoadEffect = createEffect(
  (actions$ = inject(Actions), mediaTypeService = inject(FileManagerTipologieService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.loadMediaTypes),
      concatLatestFrom(({ entity }) => store.select(fileManagerSelectors.mediaTypes(entity))),
      exhaustMap(([action, mediaTypes]) => {
        return !mediaTypes || mediaTypes.state.value !== LoadingState.SUCCESS
          ? mediaTypeService
              .mediaTypeControllerFindAll({
                entity: action.entity,
              })
              .pipe(
                mapResponse({
                  next: (r) =>
                    fileManagerAction.doneMediaTypes({
                      entity: action.entity,
                      data: r.data.list,
                      state: { value: LoadingState.SUCCESS },
                    }),
                  error: (error: ZetraError) =>
                    fileManagerAction.doneMediaTypes({
                      entity: action.entity,
                      state: {
                        value: LoadingState.FAIL,
                        message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                      },
                    }),
                }),
              )
          : of(
              fileManagerAction.doneMediaTypes({
                entity: action.entity,
                data: mediaTypes.data!,
                state: { value: LoadingState.SUCCESS },
              }),
            );
      }),
    );
  },
  { functional: true },
);

export const entityLoadOneMediaEffect = createEffect(
  (actions$ = inject(Actions), entityMediaService = inject(FileManagerEntitaService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.loadOneMedia),
      delay(1000),
      exhaustMap((props) =>
        entityMediaService.entityMediaControllerGetEntityFiles(props).pipe(
          mapResponse({
            next: (r) =>
              fileManagerAction.loadOneMediaDone({
                entity: props.entity,
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              fileManagerAction.loadOneMediaDone({
                entity: props.entity,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const entityDeleteOneMediaEffect = createEffect(
  (actions$ = inject(Actions), entityMediaService = inject(FileManagerEntitaService), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.deleteOneMedia),
      delay(1000),
      exhaustMap((props) =>
        entityMediaService.entityMediaControllerDetachAndDelete(props).pipe(
          mapResponse({
            next: (_r) => {
              return fileManagerAction.deleteOneMediaDone({
                entityId: props.entityId,
                entity: props.entity,
                data: undefined,
                mediaTypeId: props.mediaTypeId,
                state: { value: LoadingState.SUCCESS },
              });
            },
            error: (error: ZetraError) =>
              fileManagerAction.deleteOneMediaDone({
                entityId: props.entityId,
                entity: props.entity,
                mediaTypeId: props.mediaTypeId,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//  deleteOneMediaDone SUCCESS

export const entityDeleteOneMediaDoneEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(fileManagerAction.deleteOneMediaDone),
      filter((props) => props.state.value === LoadingState.SUCCESS),
      tap((props) => {
        store.dispatch(
          fileManagerAction.loadOneMedia({
            entity: props.entity,
            entityId: props.entityId,
            mediaTypeId: props.mediaTypeId,
            limit: 10,
            skip: 0,
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: false },
);
// deleteOneMediaDone SUCCESS
export const entityLoadOneMediaTypeEffect = createEffect(
  (actions$ = inject(Actions), mediaTypeService = inject(FileManagerTipologieService)) => {
    return actions$.pipe(
      ofType(fileManagerAction.loadOneMediaType),
      delay(1000),
      exhaustMap((props) =>
        mediaTypeService
          .mediaTypeControllerFindOne({
            id: props.mediaTypeId,
          })
          .pipe(
            mapResponse({
              next: (r) =>
                fileManagerAction.loadOneMediaTypeDone({
                  entity: props.entity,
                  data: r.data,
                  state: { value: LoadingState.SUCCESS },
                }),
              error: (error: ZetraError) =>
                fileManagerAction.loadOneMediaTypeDone({
                  entity: props.entity,
                  state: {
                    value: LoadingState.FAIL,
                    message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                  },
                }),
            }),
          ),
      ),
    );
  },
  { functional: true },
);
